import type { WorldFlowHop } from '@/analysis/worldFlowTypes'
import { hopKey, type PosMap } from './hopPlacement'
import { deliverySevFromMetrics, segmentSevForNode, worstTier, type WorldSegment } from './segments'

export const CLUSTER_PIXEL_RADIUS = 36
/** Below this zoom, region role stacks stay hidden — clusters carry the overview. */
export const REGION_STACK_MIN_ZOOM = 1.2
/** Low-zoom arc budget: prefer stressed links, then top by streams. */
export const LOW_ZOOM_ARC_CAP = 24
export const LOW_ZOOM_THRESHOLD = 1.35

export type HopRole = 'studio' | 'cdn_edge' | 'city'

export type ClusterMember = {
  hop: WorldFlowHop
  hopId: string
  mapPos: { x: number; y: number }
  screenPos: { x: number; y: number }
  sev: 0 | 1 | 2 | null
}

export type HopCluster = {
  id: string
  members: ClusterMember[]
  /** Map-space centroid (for arcs). */
  mapCentroid: { x: number; y: number }
  /** Screen-space centroid (for glyphs). */
  screenCentroid: { x: number; y: number }
  worstSev: 0 | 1 | 2 | null
  totalStreams: number
  usersTotal: number
  criticalCount: number
  warningCount: number
  goodCount: number
  roles: Set<HopRole>
}

export type ClusterResult = {
  clusters: HopCluster[]
  /** hopId (`type:value`) → cluster id */
  hopToCluster: Map<string, string>
  /** Map-space positions keyed by cluster id (for arcs). */
  clusterMapPositions: PosMap
}

function toScreen(
  pos: { x: number; y: number },
  pan: { x: number; y: number },
  zoom: number,
) {
  return { x: pan.x + pos.x * zoom, y: pan.y + pos.y * zoom }
}

function memberSev(h: WorldFlowHop, segment: WorldSegment): 0 | 1 | 2 | null {
  if (h.type === 'cdn_edge' && segment === 'overall') {
    return deliverySevFromMetrics(h.metrics)
  }
  if (h.type === 'studio' && segment === 'overall') {
    return deliverySevFromMetrics(h.metrics)
  }
  return segmentSevForNode(h, segment)
}

/**
 * Screen-space greedy clustering: merge hops within `radiusPx` so the overview
 * never piles studio/edge/client icons on top of each other. Zooming out merges;
 * zooming in splits. Mixed roles are allowed in one cluster.
 */
export function clusterHops(opts: {
  hops: WorldFlowHop[]
  positions: PosMap
  pan: { x: number; y: number }
  zoom: number
  segment: WorldSegment
  layers: { studios: boolean; edges: boolean; clients: boolean }
  radiusPx?: number
}): ClusterResult {
  const radius = opts.radiusPx ?? CLUSTER_PIXEL_RADIUS
  const r2 = radius * radius

  const candidates: ClusterMember[] = []
  for (const h of opts.hops) {
    if (h.type === 'studio' && !opts.layers.studios) continue
    if (h.type === 'cdn_edge' && !opts.layers.edges) continue
    if (h.type === 'city' && !opts.layers.clients) continue
    const id = hopKey(h.type, h.value)
    const mapPos = opts.positions[id]
    if (!mapPos) continue
    candidates.push({
      hop: h,
      hopId: id,
      mapPos,
      screenPos: toScreen(mapPos, opts.pan, opts.zoom),
      sev: memberSev(h, opts.segment),
    })
  }

  // Stable order: denser / larger first so anchors are meaningful.
  candidates.sort((a, b) => b.hop.totalStreams - a.hop.totalStreams)

  const assigned = new Set<string>()
  const clusters: HopCluster[] = []
  let seq = 0

  for (const seed of candidates) {
    if (assigned.has(seed.hopId)) continue
    const members: ClusterMember[] = [seed]
    assigned.add(seed.hopId)

    for (const other of candidates) {
      if (assigned.has(other.hopId)) continue
      const dx = other.screenPos.x - seed.screenPos.x
      const dy = other.screenPos.y - seed.screenPos.y
      if (dx * dx + dy * dy <= r2) {
        members.push(other)
        assigned.add(other.hopId)
      }
    }

    let sx = 0
    let sy = 0
    let mx = 0
    let my = 0
    let worstSev: 0 | 1 | 2 | null = null
    let totalStreams = 0
    let usersTotal = 0
    let criticalCount = 0
    let warningCount = 0
    let goodCount = 0
    const roles = new Set<HopRole>()
    for (const m of members) {
      sx += m.screenPos.x
      sy += m.screenPos.y
      mx += m.mapPos.x
      my += m.mapPos.y
      worstSev = worstTier(worstSev, m.sev)
      totalStreams += m.hop.totalStreams
      usersTotal += m.hop.usersTotal
      criticalCount += Number(m.hop.criticalCount ?? 0)
      warningCount += Number(m.hop.warningCount ?? 0)
      goodCount += Number(m.hop.goodCount ?? 0)
      roles.add(m.hop.type as HopRole)
    }
    const n = members.length
    const id = `c${seq++}`
    clusters.push({
      id,
      members,
      mapCentroid: { x: mx / n, y: my / n },
      screenCentroid: { x: sx / n, y: sy / n },
      worstSev,
      totalStreams,
      usersTotal,
      criticalCount,
      warningCount,
      goodCount,
      roles,
    })
  }

  const hopToCluster = new Map<string, string>()
  const clusterMapPositions: PosMap = {}
  for (const c of clusters) {
    clusterMapPositions[c.id] = c.mapCentroid
    for (const m of c.members) hopToCluster.set(m.hopId, c.id)
  }

  return { clusters, hopToCluster, clusterMapPositions }
}

/** Collapse links onto cluster endpoints; dedupe; optionally quiet low-zoom set. */
export function clusterLinks(opts: {
  links: Array<{
    from: { type: string; value: string }
    to: { type: string; value: string }
    usersTotal: number
    totalStreams: number
    avgSeverity: 0 | 1 | 2 | null
    worstMetric: string | null
    metrics: unknown
  }>
  hopToCluster: Map<string, string>
  zoom: number
  segment: WorldSegment
}): Array<{
  id: string
  fromCluster: string
  toCluster: string
  usersTotal: number
  totalStreams: number
  avgSeverity: 0 | 1 | 2 | null
  worstMetric: string | null
  metrics: unknown
  label: string
}> {
  type Acc = {
    id: string
    fromCluster: string
    toCluster: string
    usersTotal: number
    totalStreams: number
    avgSeverity: 0 | 1 | 2 | null
    worstMetric: string | null
    metrics: unknown
    label: string
  }
  const byPair = new Map<string, Acc>()

  for (const link of opts.links) {
    const fromId = hopKey(link.from.type, link.from.value)
    const toId = hopKey(link.to.type, link.to.value)
    const fromCluster = opts.hopToCluster.get(fromId)
    const toCluster = opts.hopToCluster.get(toId)
    if (!fromCluster || !toCluster) continue
    if (fromCluster === toCluster) continue // internal to one pile — skip

    const key = `${fromCluster}->${toCluster}`
    const sev = segmentSevForNode(
      { avgSeverity: link.avgSeverity, metrics: link.metrics as never },
      opts.segment,
    )
    const prev = byPair.get(key)
    if (!prev) {
      byPair.set(key, {
        id: key,
        fromCluster,
        toCluster,
        usersTotal: link.usersTotal,
        totalStreams: link.totalStreams,
        avgSeverity: sev,
        worstMetric: link.worstMetric,
        metrics: link.metrics,
        label: `${fromCluster} → ${toCluster}`,
      })
    } else {
      prev.usersTotal += link.usersTotal
      prev.totalStreams += link.totalStreams
      prev.avgSeverity = worstTier(prev.avgSeverity, sev)
    }
  }

  let list = [...byPair.values()]
  if (opts.zoom < LOW_ZOOM_THRESHOLD) {
    // Prefer stressed arcs, then by stream volume; hard cap for overview.
    list.sort((a, b) => {
      const sa = a.avgSeverity == null ? 2 : a.avgSeverity
      const sb = b.avgSeverity == null ? 2 : b.avgSeverity
      if (sa !== sb) return sa - sb
      return b.totalStreams - a.totalStreams
    })
    list = list.slice(0, LOW_ZOOM_ARC_CAP)
  }
  return list
}
